/**
 * REST client for the Django backend.
 *
 * JWTs live in localStorage (shared by every eval app on the origin). A 401
 * triggers ONE awaited refresh (`/api/auth/token/refresh/`), shared by all
 * concurrent callers (single flight), then the request is retried once. If
 * the refresh fails the session is cleared and `onSignedOut` listeners fire.
 */

import { apiBase } from './env';
import type { AnnotationDTO, CarryState, CommentDTO, DocumentDTO, DriveFileDTO, SlidesOutdatedDTO, UserDTO } from './types';
import type {
  AiAccessDTO,
  ApiTokenCreated,
  ApiTokenDTO,
  ReplyStatus,
  ReviewDTO,
  ReviewPayload,
  ReviewStatus,
  SlideChangeDTO,
  SlideRevisionDTO,
  SlidesSnapshotDTO,
} from './slides';

export interface SlidesRefreshResult {
  revision_id: string | null;
  changed: boolean;
  page_count: number;
  missing: string[];
  changes: SlideChangeDTO[];
  carryCounts: Partial<Record<CarryState, number>>;
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function memoryStore(): KeyValueStore {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
}

function defaultStore(): KeyValueStore {
  try {
    if (typeof localStorage !== 'undefined') {
      const k = '__redline_probe__';
      localStorage.setItem(k, '1');
      localStorage.removeItem(k);
      return localStorage;
    }
  } catch {
    /* private mode / blocked storage */
  }
  return memoryStore();
}

export interface Session {
  access: string;
  refresh: string;
  user: { id: string; name: string; email: string };
}

const KEYS = {
  access: 'redline.auth.access',
  refresh: 'redline.auth.refresh',
  userId: 'redline.auth.userId',
  userName: 'redline.auth.userName',
  userEmail: 'redline.auth.userEmail',
};

export class TokenStore {
  constructor(private readonly kv: KeyValueStore = defaultStore()) {}

  get access(): string | null {
    return this.kv.getItem(KEYS.access);
  }
  get refresh(): string | null {
    return this.kv.getItem(KEYS.refresh);
  }
  get session(): Session | null {
    const access = this.access;
    const refresh = this.refresh;
    const id = this.kv.getItem(KEYS.userId);
    if (!access || !refresh || !id) return null;
    return {
      access,
      refresh,
      user: { id, name: this.kv.getItem(KEYS.userName) ?? '', email: this.kv.getItem(KEYS.userEmail) ?? '' },
    };
  }
  set(s: Session): void {
    this.kv.setItem(KEYS.access, s.access);
    this.kv.setItem(KEYS.refresh, s.refresh);
    this.kv.setItem(KEYS.userId, s.user.id);
    this.kv.setItem(KEYS.userName, s.user.name);
    this.kv.setItem(KEYS.userEmail, s.user.email);
  }
  setAccess(access: string): void {
    this.kv.setItem(KEYS.access, access);
  }
  clear(): void {
    for (const k of Object.values(KEYS)) this.kv.removeItem(k);
  }
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function errorFrom(res: Response): Promise<ApiError> {
  let body: unknown = null;
  let message = `HTTP ${res.status}`;
  try {
    const text = await res.text();
    try {
      body = JSON.parse(text);
      const b = body as { error?: string; detail?: string };
      message = b.error || b.detail || message;
    } catch {
      body = text;
      if (text && text.length < 200) message = text;
    }
  } catch {
    /* ignore */
  }
  return new ApiError(res.status, message, body);
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface ApiClientOptions {
  baseUrl?: string;
  tokens?: TokenStore;
  fetch?: typeof fetch;
}

export interface DriveListing {
  files: DriveFileDTO[];
  next_page_token: string | null;
  /** Root listing only: the eval folders, pinned above My Drive & shared drives. */
  shortcuts?: DriveFileDTO[];
}

/** First column of the Drive browser: My Drive, Shared with me, each shared drive. */
export interface DriveRootDTO {
  /** 'root' = My Drive, 'shared-with-me', or the shared drive's id (all valid `folder_id`s). */
  id: string;
  name: string;
  kind: 'my_drive' | 'shared_with_me' | 'shared_drive';
}

/** Search over folders + PDFs (folders first); every row has a `location` line. */
export interface DriveSearchListing extends DriveListing {
  /** 'subtree' when `within` was set: that folder and everything below it. */
  scope: 'all' | 'subtree';
  /** In-folder search covered only part of a very large subtree. */
  truncated: boolean;
}

/** Where a Drive item lives: the browser root it's under and the folders below that, down to its parent. */
export interface DrivePathDTO {
  item: DriveFileDTO;
  root: DriveRootDTO;
  path: { id: string; name: string }[];
}

/** Largest PDF the upload endpoint takes (backend PDF_UPLOAD_MAX_BYTES; nginx allows 50 MB bodies). */
export const PDF_UPLOAD_MAX_BYTES = 49 * 1024 * 1024;

/** Largest picture for an image markup (backend DOCUMENT_IMAGE_MAX_BYTES). */
export const IMAGE_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

/** A picture uploaded for an image markup (`POST /api/documents/<id>/images/`). */
export interface DocumentImageDTO {
  id: string;
  document_id: string;
  mime: string;
  size: number;
  /** Pixels after the server's downscale / EXIF rotation; null for an SVG without a size. */
  width: number | null;
  height: number | null;
  /** Authenticated API path (fetch with the JWT — `ApiClient.imageBlob`). */
  url: string;
}

/** A Drive folder row for the "Save to Drive" picker (same shape as the whiteboard picker's). */
export interface DriveFolderRow {
  id: string;
  name: string;
  parent_id?: string | null;
  path?: string;
  can_add: boolean;
  shared_drive?: boolean;
  used?: boolean;
  /** Search rows: where the folder lives ("Job › 01-Inbox"). */
  location?: string;
}

export interface ImportResult {
  document_id: string;
  already_exists: boolean;
  download_url: string | null;
}

export class ApiClient {
  readonly baseUrl: string;
  readonly tokens: TokenStore;
  private readonly fetchImpl: typeof fetch;
  private refreshInFlight: Promise<string | null> | null = null;
  private signedOutListeners = new Set<() => void>();
  private sessionListeners = new Set<(s: Session | null) => void>();

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? apiBase()).replace(/\/+$/, '');
    this.tokens = opts.tokens ?? new TokenStore();
    this.fetchImpl = opts.fetch ?? ((...a) => fetch(...a));
  }

  url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  // ---- session events ----------------------------------------------------

  /** Fires when a refresh fails (or signOut is called). */
  onSignedOut(cb: () => void): () => void {
    this.signedOutListeners.add(cb);
    return () => this.signedOutListeners.delete(cb);
  }
  onSessionChange(cb: (s: Session | null) => void): () => void {
    this.sessionListeners.add(cb);
    return () => this.sessionListeners.delete(cb);
  }
  setSession(s: Session): void {
    this.tokens.set(s);
    this.sessionListeners.forEach((l) => l(s));
  }
  signOut(): void {
    this.tokens.clear();
    this.sessionListeners.forEach((l) => l(null));
    this.signedOutListeners.forEach((l) => l());
  }

  /**
   * Single-flight refresh: concurrent callers share one network request.
   * Resolves to the new access token, or null if the session is dead.
   */
  refreshAccessToken(): Promise<string | null> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const refresh = this.tokens.refresh;
    if (!refresh) return Promise.resolve(null);
    this.refreshInFlight = (async () => {
      try {
        const res = await this.fetchImpl(this.url('/api/auth/token/refresh/'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { access?: string };
        if (!data.access) return null;
        this.tokens.setAccess(data.access);
        return data.access;
      } catch {
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();
    return this.refreshInFlight;
  }

  /** Authenticated fetch with one refresh-and-retry on 401. Throws ApiError on !ok. */
  async request(path: string, init: RequestInit = {}, opts: { auth?: boolean } = {}): Promise<Response> {
    const auth = opts.auth !== false;
    const send = (token: string | null) => {
      const headers = new Headers(init.headers);
      if (auth && token) headers.set('Authorization', `Bearer ${token}`);
      return this.fetchImpl(this.url(path), { ...init, headers });
    };
    let res = await send(auth ? this.tokens.access : null);
    if (res.status === 401 && auth) {
      const fresh = await this.refreshAccessToken();
      if (!fresh) {
        this.signOut();
        throw await errorFrom(res);
      }
      res = await send(fresh);
      if (res.status === 401) {
        this.signOut();
        throw await errorFrom(res);
      }
    }
    if (!res.ok) throw await errorFrom(res);
    return res;
  }

  async json<T>(path: string, init: RequestInit = {}, opts: { auth?: boolean } = {}): Promise<T> {
    const res = await this.request(path, init, opts);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private body(method: string, data: unknown): RequestInit {
    return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) };
  }

  // ---- auth ----------------------------------------------------------------

  me(): Promise<UserDTO> {
    return this.json('/api/auth/me/');
  }

  /** DEBUG-only backend endpoint used by the local QA harness. */
  async serviceAccountLogin(email: string, name: string, apiKey: string): Promise<Session> {
    const data = await this.json<{ access: string; refresh: string; user: UserDTO }>(
      '/api/auth/service-account/',
      this.body('POST', { api_key: apiKey, email, name }),
      { auth: false },
    );
    const session: Session = {
      access: data.access,
      refresh: data.refresh,
      user: { id: data.user.id, name: data.user.name, email: data.user.email },
    };
    this.setSession(session);
    return session;
  }

  // ---- documents -----------------------------------------------------------

  async listDocuments(): Promise<DocumentDTO[]> {
    return (await this.json<{ documents: DocumentDTO[] }>('/api/documents/')).documents;
  }

  getDocument(id: string): Promise<DocumentDTO> {
    return this.json(`/api/documents/${id}/`);
  }

  /** The PDF bytes as served by the backend (Drive copy, or rasterized cache). */
  async downloadDocument(id: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    const res = await this.request(`/api/documents/${id}/download/`, { signal });
    return res.arrayBuffer();
  }

  /** Drive browser: My Drive & shared drives; the root listing also carries the eval folders as `shortcuts`. */
  listDriveFiles(opts: { folderId?: string | null; pageToken?: string | null; q?: string | null } = {}): Promise<DriveListing> {
    const params = new URLSearchParams();
    if (opts.folderId) params.set('folder_id', opts.folderId);
    if (opts.pageToken) params.set('page_token', opts.pageToken);
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    return this.json(`/api/documents/drive-files/${qs ? `?${qs}` : ''}`);
  }

  /** The Drive browser's first column (+ the eval folders as shortcuts). */
  driveRoots(): Promise<{ roots: DriveRootDTO[]; shortcuts: DriveFileDTO[] }> {
    return this.json('/api/documents/drive-files/?roots=1');
  }

  /** Name search returning folders and PDFs (folders first). `within` limits it to that folder's subtree. */
  searchDrive(opts: { q: string; within?: string | null; pageToken?: string | null }): Promise<DriveSearchListing> {
    const params = new URLSearchParams({ q: opts.q, folders: '1' });
    if (opts.within) params.set('within', opts.within);
    if (opts.pageToken) params.set('page_token', opts.pageToken);
    return this.json(`/api/documents/drive-files/?${params}`);
  }

  /** A Drive item's real location ("Show in enclosing folder"). */
  drivePath(id: string): Promise<DrivePathDTO> {
    return this.json(`/api/documents/drive-files/path/?id=${encodeURIComponent(id)}`);
  }

  importFromDrive(driveFileId: string, title?: string): Promise<ImportResult> {
    return this.json('/api/documents/import-from-drive/', this.body('POST', { drive_file_id: driveFileId, title: title ?? '' }));
  }

  /**
   * Upload a PDF from the computer. The server keeps it ("Not in Drive yet")
   * until someone saves it to a Drive folder (`saveToDrive`). XHR rather than
   * fetch so the caller gets upload progress (0–1).
   */
  async uploadPdf(file: File, opts: { title?: string; onProgress?(fraction: number): void; signal?: AbortSignal } = {}): Promise<ImportResult> {
    return this.uploadFile('/api/documents/upload/', file, {
      ...opts,
      fields: opts.title ? { title: opts.title } : undefined,
      tooBig: `That file is too big. PDFs can be up to ${Math.round(PDF_UPLOAD_MAX_BYTES / 1048576)} MB.`,
    });
  }

  /**
   * Upload a picture for an image markup (multipart `file`). The server
   * checks the type (PNG, JPEG, GIF, WebP, safe SVG), the size
   * (IMAGE_UPLOAD_MAX_BYTES → 413) and scales huge pictures down; nothing
   * is placed until an 'image' annotation with the returned id is created.
   */
  uploadImage(documentId: string, file: File, opts: { onProgress?(fraction: number): void; signal?: AbortSignal } = {}): Promise<DocumentImageDTO> {
    return this.uploadFile(`/api/documents/${documentId}/images/`, file, {
      ...opts,
      tooBig: `That image is too big. Images can be up to ${Math.round(IMAGE_UPLOAD_MAX_BYTES / 1048576)} MB.`,
    });
  }

  /** An image markup's picture. Never a public URL: fetched with the JWT like any API call. */
  async imageBlob(documentId: string, imageId: string, signal?: AbortSignal): Promise<Blob> {
    const res = await this.request(`/api/documents/${documentId}/images/${imageId}/`, { signal });
    return res.blob();
  }

  /** Multipart POST of one file over XHR (upload progress), with the same one-refresh retry as `request`. */
  private async uploadFile<T>(
    path: string,
    file: File,
    opts: { fields?: Record<string, string>; onProgress?(fraction: number): void; signal?: AbortSignal; tooBig: string },
  ): Promise<T> {
    const send = (token: string | null) =>
      new Promise<{ status: number; text: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this.url(path));
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => e.lengthComputable && opts.onProgress?.(e.loaded / e.total);
        xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
        xhr.onerror = () => reject(new ApiError(0, 'Could not reach the server. Check your connection and try again.'));
        xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
        opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
        const fd = new FormData();
        fd.append('file', file, file.name);
        for (const [k, v] of Object.entries(opts.fields ?? {})) fd.append(k, v);
        xhr.send(fd);
      });
    let res = await send(this.tokens.access);
    if (res.status === 401) {
      const fresh = await this.refreshAccessToken();
      if (!fresh) {
        this.signOut();
        throw new ApiError(401, 'Signed out');
      }
      res = await send(fresh);
    }
    let body: unknown = null;
    try {
      body = JSON.parse(res.text);
    } catch {
      /* nginx error pages are HTML */
    }
    if (res.status < 200 || res.status >= 300) {
      const b = (body ?? {}) as { error?: string; detail?: string };
      const message = b.error || b.detail || (res.status === 413 ? opts.tooBig : `Upload failed (HTTP ${res.status})`);
      throw new ApiError(res.status, message, body);
    }
    return body as T;
  }

  /** Put an uploaded ("Not in Drive yet") PDF, annotations included, into a Drive folder. */
  saveToDrive(documentId: string, folderId: string): Promise<DocumentDTO> {
    return this.json(`/api/documents/${documentId}/save-to-drive/`, this.body('POST', { folder_id: folderId }));
  }

  /** Folder picker rows for "Save to Drive": My Drive & shared drives, search, eval folders as shortcuts. */
  driveFolders(opts: { parent?: string | null; q?: string } = {}): Promise<{ folders: DriveFolderRow[]; recent: DriveFolderRow[]; shortcuts: DriveFolderRow[] }> {
    const p = new URLSearchParams();
    if (opts.parent) p.set('parent', opts.parent);
    if (opts.q) p.set('q', opts.q);
    const qs = p.toString();
    return this.json(`/api/documents/drive-folders/${qs ? `?${qs}` : ''}`);
  }

  /** A folder's real path for the "Save to Drive" picker (opening a search hit or shortcut). */
  driveFolderTrail(folderId: string): Promise<{ trail: { id: string; name: string }[] }> {
    return this.json(`/api/documents/drive-folders/?trail=${encodeURIComponent(folderId)}`);
  }

  /**
   * "Save to Drive now". 202 = queued; the outcome arrives over the socket as
   * `drive.sync_result` — never treat this response as "saved".
   */
  async syncPdf(documentId: string): Promise<{ queued: boolean; status: string }> {
    try {
      const data = await this.json<{ status: string }>(`/api/documents/${documentId}/sync-pdf/`, { method: 'POST' });
      return { queued: true, status: data?.status ?? 'queued' };
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) return { queued: false, status: 'queue_unavailable' };
      throw e;
    }
  }

  // ---- annotations (REST fallback; live edits go over the socket) ---------

  async listAnnotations(documentId: string): Promise<AnnotationDTO[]> {
    const data = await this.json<AnnotationDTO[] | { annotations: AnnotationDTO[] }>(`/api/documents/${documentId}/annotations/`);
    return Array.isArray(data) ? data : data.annotations;
  }

  // ---- comments --------------------------------------------------------------

  listComments(documentId: string, annotationId: string): Promise<CommentDTO[]> {
    return this.json(`/api/documents/${documentId}/annotations/${annotationId}/comments/`);
  }
  createComment(documentId: string, annotationId: string, text: string, parentId?: string | null): Promise<CommentDTO> {
    return this.json(
      `/api/documents/${documentId}/annotations/${annotationId}/comments/`,
      this.body('POST', { text, parent_id: parentId ?? null }),
    );
  }
  updateComment(documentId: string, annotationId: string, commentId: string, text: string): Promise<CommentDTO> {
    return this.json(`/api/documents/${documentId}/annotations/${annotationId}/comments/${commentId}/`, this.body('PUT', { text }));
  }
  async deleteComment(documentId: string, annotationId: string, commentId: string): Promise<void> {
    await this.request(`/api/documents/${documentId}/annotations/${annotationId}/comments/${commentId}/`, { method: 'DELETE' });
  }

  // ---- Google Slides (presentation review; eval/SLIDES_PLAN.md) -------------

  /** Import a deck by link or id. Resolves to the new (or existing) document. */
  async importSlides(urlOrId: string): Promise<DocumentDTO> {
    const isUrl = /^https?:/i.test(urlOrId.trim());
    const data = await this.json<DocumentDTO | { document: DocumentDTO }>(
      '/api/documents/import-slides/',
      this.body('POST', isUrl ? { url: urlOrId.trim() } : { presentation_id: urlOrId.trim() }),
    );
    return 'document' in data && data.document ? data.document : (data as DocumentDTO);
  }

  getSlides(documentId: string, signal?: AbortSignal): Promise<SlidesSnapshotDTO> {
    return this.json(`/api/documents/${documentId}/slides/`, { signal });
  }

  /** Re-export the deck; the PDF content changes in place (reload the editor afterwards). */
  async refreshSlides(documentId: string, opts: { force?: boolean; simulate?: Record<string, unknown> } = {}): Promise<SlidesRefreshResult> {
    const r = await this.json<{
      revision_id?: string | null;
      changed?: boolean;
      page_count?: number;
      slide_missing_annotation_ids?: string[];
      missing_slide_annotation_ids?: string[];
      changes?: SlideChangeDTO[];
      carry_counts?: Partial<Record<CarryState, number>>;
    }>(`/api/documents/${documentId}/refresh-slides/`, this.body('POST', opts));
    return {
      revision_id: r.revision_id ?? null,
      changed: r.changed !== false,
      page_count: r.page_count ?? 0,
      missing: r.slide_missing_annotation_ids ?? r.missing_slide_annotation_ids ?? [],
      changes: r.changes ?? [],
      carryCounts: r.carry_counts ?? {},
    };
  }

  /** Has the deck changed in Google Slides since our export? (Runs a check now.) */
  async checkSlides(documentId: string, simulate?: { modified_time?: string; modified_by?: string }): Promise<SlidesOutdatedDTO | null> {
    const r = await this.json<{ outdated?: SlidesOutdatedDTO | null }>(
      `/api/documents/${documentId}/slides/check/`,
      this.body('POST', simulate ? { simulate } : {}),
    );
    return r.outdated ?? null;
  }

  /** Every kept revision of the deck, newest first. */
  getSlideRevisions(documentId: string, signal?: AbortSignal): Promise<SlideRevisionDTO[]> {
    return this.json(`/api/documents/${documentId}/slides/revisions/`, { signal });
  }

  /** One slide of a kept revision (clean, no markups) as an object URL. Caller revokes it. */
  async revisionSlideImage(documentId: string, snapshotId: string, index: number, signal?: AbortSignal): Promise<string> {
    const res = await this.request(`/api/documents/${documentId}/slides/revisions/${snapshotId}/${index}.png`, { signal });
    return URL.createObjectURL(await res.blob());
  }

  // ---- reviews ("Send to AI") ----------------------------------------------

  createReview(documentId: string, message = ''): Promise<ReviewDTO> {
    return this.json(`/api/documents/${documentId}/reviews/`, this.body('POST', { message }));
  }

  /** A document's reviews (newest first), or every review the user can see. */
  async listReviews(documentId?: string): Promise<ReviewDTO[]> {
    const path = documentId ? `/api/documents/${documentId}/reviews/` : '/api/reviews/';
    const data = await this.json<ReviewDTO[] | { reviews: ReviewDTO[]; results?: ReviewDTO[] }>(path);
    const rows = Array.isArray(data) ? data : data.reviews ?? data.results ?? [];
    return documentId ? rows.filter((r) => !r.document_id || r.document_id === documentId) : rows;
  }

  getReview(reviewId: string): Promise<ReviewPayload> {
    return this.json(`/api/reviews/${reviewId}/`);
  }

  setReviewStatus(reviewId: string, status: ReviewStatus): Promise<ReviewDTO> {
    return this.json(`/api/reviews/${reviewId}/`, this.body('PATCH', { status }));
  }

  reviewApiUrl(reviewId: string): string {
    return absolute(this.url(`/api/reviews/${reviewId}/`));
  }
  reviewBundleUrl(reviewId: string): string {
    return absolute(this.url(`/api/reviews/${reviewId}/bundle.zip`));
  }
  mcpUrl(): string {
    return absolute(this.url('/api/mcp/'));
  }

  /** The bundle needs auth, so it's fetched as a Blob rather than linked. */
  async downloadReviewBundle(reviewId: string): Promise<Blob> {
    const res = await this.request(`/api/reviews/${reviewId}/bundle.zip`);
    return res.blob();
  }

  replyToMarkup(reviewId: string, annotationId: string, text: string, status?: ReplyStatus): Promise<CommentDTO | Record<string, unknown>> {
    return this.json(`/api/reviews/${reviewId}/markups/${annotationId}/reply/`, this.body('POST', status ? { text, status } : { text }));
  }

  // ---- personal API tokens (AI access) ---------------------------------------

  async listTokens(): Promise<ApiTokenDTO[]> {
    const data = await this.json<ApiTokenDTO[] | { tokens: ApiTokenDTO[] }>('/api/tokens/');
    return Array.isArray(data) ? data : data.tokens;
  }
  createToken(name: string): Promise<ApiTokenCreated> {
    return this.json('/api/tokens/', this.body('POST', { name }));
  }
  async revokeToken(id: string): Promise<void> {
    await this.request(`/api/tokens/${id}/`, { method: 'DELETE' });
  }

  // ---- connected AI clients (OAuth) -------------------------------------------

  getAiAccess(): Promise<AiAccessDTO> {
    return this.json('/api/ai-access/');
  }
  async disconnectAiClient(id: string): Promise<void> {
    await this.request(`/api/ai-clients/${id}/`, { method: 'DELETE' });
  }
}

/** Resolve a possibly-relative API URL against the page (for copy-paste to an AI). */
function absolute(u: string): string {
  if (/^https?:/i.test(u) || typeof location === 'undefined') return u;
  return new URL(u, location.origin).toString();
}

/** Process-wide default client (uses the env-derived base URL + localStorage). */
let defaultClient: ApiClient | null = null;
export function getApi(): ApiClient {
  if (!defaultClient) defaultClient = new ApiClient();
  return defaultClient;
}
